"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import Image from "next/image";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ThumbsUp, RefreshCcw, Loader2, TriangleAlert } from "lucide-react";
import { normalizeCommunityPreviewUrl } from "@/lib/community/preview-url";
import {
  isVisitorBlockingReason,
  type CommunityPreviewHealth,
} from "@/lib/community/preview-health";
import {
  trackTemplatePreviewViewed,
  trackTemplatePreviewError,
  type TemplatePreviewErrorReason,
} from "@/lib/analytics";

interface PreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  title: string;
  vercelUrl?: string | null;
  screenshotSrc?: string;
  likes?: number;
  hasLiked?: boolean;
  remixCount?: number;
  isRemixing?: boolean;
  onRemix?: (projectId: string) => void;
  onLikeToggle?: () => void;
}

// Cross-origin frame blocks (X-Frame-Options / CSP frame-ancestors) don't
// fire a synchronous error event, so we treat "onLoad never fired in 8s"
// as the failure signal and swap to the screenshot.
const IFRAME_LOAD_TIMEOUT_MS = 8000;

/**
 * How long we'll wait for the health verdict before revealing the iframe anyway.
 * Sits just past the server probe's own 8s timeout so a probe that is merely slow
 * still gets to answer. On expiry we reveal — same fail-toward-healthy contract
 * the classifier follows: an unanswered probe is ambiguous, not a failure.
 */
const HEALTH_DEADLINE_MS = 9000;

type HealthState =
  | { state: "pending" }
  | { state: "ok" }
  | {
      state: "broken";
      reason: TemplatePreviewErrorReason;
      status: number | null;
    };

/**
 * The community gallery's live preview.
 *
 * A cross-origin iframe cannot report its HTTP status, and a 404 page LOADS
 * SUCCESSFULLY — `onLoad` fires for it exactly as it does for a healthy app. So
 * "the frame loaded" was never evidence the preview worked, and a deployment
 * that had since been deleted or detached rendered the platform's raw
 * "404: NOT_FOUND" inside this modal (GT-02). The listing only proves a project
 * was deployed once; nothing re-checks that the URL still resolves.
 *
 * Two things fix that here:
 *   1. the URL is normalized to an absolute one, so a stored bare host can't
 *      resolve relative to panda.new and 404 on our own site; and
 *   2. /api/community-projects/[id]/preview-health answers the status question
 *      from the server, where it IS readable.
 *
 * The screenshot overlay that already covered the loading frame now stays up
 * until the verdict is in, so a dead deployment is never revealed at all — the
 * visitor sees the design and a "Clone this template" CTA instead of an error
 * page. The onLoad watchdog remains, since only the client can see a frame the
 * server-side probe would call healthy but the browser refuses to embed.
 */
export function PreviewModal({
  isOpen,
  onClose,
  projectId,
  title,
  vercelUrl,
  screenshotSrc,
  likes = 0,
  hasLiked = false,
  remixCount = 0,
  isRemixing = false,
  onRemix,
  onLikeToggle,
}: PreviewModalProps) {
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [frameBlocked, setFrameBlocked] = useState(false);
  const [health, setHealth] = useState<HealthState>({ state: "pending" });
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  // A bare host ("my-app.panda.new") would resolve RELATIVE to the current page
  // and frame our own 404. Normalizing also rejects any non-http scheme, which
  // must never reach an iframe `src`.
  const frameUrl = normalizeCommunityPreviewUrl(vercelUrl);

  // Adjust state during render when the previewed project/URL/open-state
  // changes — React's recommended pattern for resetting derived state on
  // prop change, avoiding the cascading-render lint rule that fires when
  // setState is called synchronously inside useEffect.
  const previewKey = `${projectId}|${frameUrl ?? ""}|${isOpen ? "open" : "closed"}`;
  const [prevPreviewKey, setPrevPreviewKey] = useState<string | null>(null);
  if (previewKey !== prevPreviewKey) {
    setPrevPreviewKey(previewKey);
    setIframeLoaded(false);
    setFrameBlocked(false);
    setHealth({ state: "pending" });
  }

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // One error event per open, whatever order the failures arrive in — the KPI is
  // a rate over opens, so a single broken preview must not report twice.
  const errorReportedRef = useRef(false);
  const reportError = useCallback(
    (reason: TemplatePreviewErrorReason, httpStatus: number | null) => {
      if (errorReportedRef.current) return;
      errorReportedRef.current = true;
      trackTemplatePreviewError({
        projectId,
        title,
        remixCount,
        surface: "community_gallery",
        reason,
        httpStatus,
        fellBackToScreenshot: !!screenshotSrc,
      });
    },
    [projectId, title, remixCount, screenshotSrc],
  );

  // Held in a ref so the effects below can call it WITHOUT listing it as a
  // dependency. It closes over card metadata (remixCount changes the moment the
  // visitor clones), and a new identity mid-open would re-arm the load watchdog
  // under a frame that had already loaded — arming an 8s timer that then tears
  // down a working preview and reports a frame_blocked that never happened.
  const reportErrorRef = useRef(reportError);
  useEffect(() => {
    reportErrorRef.current = reportError;
  }, [reportError]);

  // Fires on every open, before the outcome is known — it's the denominator of
  // the error rate, so the failing opens have to be in it too.
  useEffect(() => {
    if (!isOpen) return;
    errorReportedRef.current = false;
    trackTemplatePreviewViewed({
      projectId,
      title,
      remixCount,
      surface: "community_gallery",
    });
    // remixCount/title are card metadata that don't change while open; keying on
    // them would re-fire the event on an unrelated re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, projectId]);

  // A card with no framable URL is a broken preview too — count it rather than
  // letting the empty state absorb it silently.
  useEffect(() => {
    if (!isOpen || frameUrl) return;
    reportErrorRef.current("not_published", null);
  }, [isOpen, frameUrl]);

  // Ask the server whether the deployment is actually serving. Runs in parallel
  // with the iframe, so a healthy preview pays no extra round-trip of latency —
  // the screenshot overlay is already up while both are in flight.
  useEffect(() => {
    if (!isOpen || !frameUrl) return;

    const controller = new AbortController();
    // Distinguishes "we tore this effect down" from "the probe failed": only the
    // latter should resolve the state, or a teardown would mark the NEXT
    // project's unverified frame healthy.
    let cancelled = false;
    // Ambiguity resolves toward healthy: a probe that never answers must not be
    // the reason a working preview stays hidden.
    const deadline = setTimeout(() => {
      controller.abort();
      if (mountedRef.current) setHealth({ state: "ok" });
    }, HEALTH_DEADLINE_MS);

    fetch(`/api/community-projects/${projectId}/preview-health`, {
      signal: controller.signal,
    })
      .then(async (res) => {
        const verdict = (await res.json()) as CommunityPreviewHealth;
        if (cancelled || !mountedRef.current) return;
        if (verdict.healthy) {
          setHealth({ state: "ok" });
          return;
        }
        // Only affirmative "this is dead" verdicts pull the preview. Anything
        // else the classifier can return (redirect, protected, client_status,
        // unreadable, unreachable) is ambiguous and keeps the frame.
        if (!isVisitorBlockingReason(verdict.reason)) {
          setHealth({ state: "ok" });
          return;
        }
        const reason = verdict.reason as TemplatePreviewErrorReason;
        setHealth({ state: "broken", reason, status: verdict.status });
        reportErrorRef.current(reason, verdict.status);
      })
      .catch(() => {
        // Aborted by the deadline above (already handled), or the probe itself
        // failed. Either way, don't punish the preview for our own outage —
        // reveal it and let the onLoad watchdog backstop a truly dead frame.
        if (cancelled || !mountedRef.current) return;
        setHealth({ state: "ok" });
      });

    return () => {
      cancelled = true;
      clearTimeout(deadline);
      controller.abort();
    };
  }, [isOpen, projectId, frameUrl]);

  // Arm the load-timeout whenever the previewed project (or its live URL)
  // changes. Cleanup clears the timer on unmount and on dep change so we
  // never setState after unmount.
  useEffect(() => {
    if (!isOpen || !frameUrl) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      if (!mountedRef.current) return;
      setFrameBlocked(true);
      reportErrorRef.current("frame_blocked", null);
    }, IFRAME_LOAD_TIMEOUT_MS);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isOpen, projectId, frameUrl]);

  const handleIframeLoad = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (!mountedRef.current) return;
    setIframeLoaded(true);
  };

  // Two distinct failures, because they warrant different copy: a card with no
  // framable URL at all never had a live site, whereas one whose deployment has
  // stopped serving did — and telling a visitor "no preview yet" about an app
  // that was live last week is its own small lie. Both are still failures for
  // the KPI, and both keep the clone CTA, which is what the visitor came for.
  const failure: "no_live_site" | "not_serving" | null = !frameUrl
    ? "no_live_site"
    : frameBlocked || health.state === "broken"
      ? "not_serving"
      : null;
  const showEmptyState = !!failure && !screenshotSrc;
  const showScreenshotFallback = !!failure && !!screenshotSrc;
  const showIframe = !!frameUrl && !failure;
  // The frame is only uncovered once it has loaded AND the server has confirmed
  // it is serving. Holding the screenshot over a healthy-but-unverified frame
  // costs nothing — it's the same design — while revealing an unverified one is
  // exactly how a 404 page reached the visitor.
  const revealIframe = iframeLoaded && health.state === "ok";

  const failureHeadline =
    failure === "no_live_site"
      ? "Preview not available"
      : "This preview isn't loading";
  const failureDetail =
    failure === "no_live_site"
      ? "This project has no live site to show. You can still clone it and run it yourself."
      : "The live site isn't responding right now. You can still clone the template and run it yourself.";

  const cloneButton = (
    <Button
      // `success`, not the brand gradient: this is the non-destructive primary
      // of a modal, and orange→red is reserved for destructive confirmations
      // here (PANDA-1779, pinned by modal-non-destructive-palette.test.ts).
      variant="success"
      onClick={() => {
        onRemix?.(projectId);
        onClose();
      }}
      disabled={isRemixing}
      aria-busy={isRemixing}
      className="gap-2"
    >
      {isRemixing ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <RefreshCcw className="h-4 w-4" />
      )}
      {isRemixing ? "Cloning…" : "Clone this template"}
    </Button>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="flex h-[90vh] w-full flex-col gap-0 overflow-hidden p-0 sm:!max-w-[70vw]"
        showCloseButton={false}
      >
        <DialogHeader className="shrink-0 min-w-0 border-b p-4">
          <div className="flex min-w-0 items-center justify-between gap-3">
            <DialogTitle className="min-w-0 truncate text-lg font-semibold">
              {title}
            </DialogTitle>
            <div className="flex shrink-0 items-center gap-2">
              {/* Remix button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  onRemix?.(projectId);
                  onClose();
                }}
                disabled={isRemixing}
                aria-busy={isRemixing}
                className="gap-1.5"
              >
                {isRemixing ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RefreshCcw className="h-3.5 w-3.5" />
                )}
                Remix
                {remixCount > 0 && (
                  <span className="text-xs text-zinc-400">({remixCount})</span>
                )}
              </Button>

              {/* Like button */}
              <Button
                variant="outline"
                size="sm"
                onClick={onLikeToggle}
                className={`gap-1.5 ${
                  hasLiked
                    ? "text-emerald-700 border-emerald-200 bg-emerald-50 hover:bg-emerald-100 dark:text-emerald-400 dark:border-emerald-900 dark:bg-emerald-950/30"
                    : ""
                }`}
              >
                <ThumbsUp className={`h-3.5 w-3.5 ${hasLiked ? "fill-current" : ""}`} />
                {likes}
              </Button>

              {/* Cancel button */}
              <Button
                variant="outline"
                size="sm"
                onClick={onClose}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="relative min-h-0 min-w-0 flex-1 bg-white dark:bg-[#12131a]">
          {showEmptyState && (
            <div className="flex h-full items-center justify-center bg-white dark:bg-[#12131a]">
              <div className="flex flex-col items-center gap-4 text-center px-8">
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-zinc-50 dark:bg-white/[0.04]">
                  <div className="h-7 w-7 rounded bg-zinc-300 dark:bg-zinc-600" />
                </div>
                <div>
                  <p className="text-[16px] font-semibold text-zinc-900 dark:text-white">
                    {failureHeadline}
                  </p>
                  <p className="mt-2 max-w-[320px] text-[14px] leading-relaxed text-zinc-400">
                    {failureDetail}
                  </p>
                </div>
                {cloneButton}
              </div>
            </div>
          )}

          {showScreenshotFallback && screenshotSrc && (
            <div className="relative h-full w-full overflow-hidden">
              <Image
                src={screenshotSrc}
                alt={`${title} screenshot`}
                fill
                className="object-cover object-top"
                unoptimized
                sizes="70vw"
              />
              {/* Say what the visitor is looking at. Showing a screenshot in place
                  of a live site without saying so is the same lie as showing the
                  404 was — and the clone CTA is the action they came for, so it
                  travels with the notice rather than being lost in the header. */}
              <div className="absolute inset-x-0 bottom-0 flex flex-wrap items-center justify-between gap-3 border-t border-white/10 bg-zinc-900/90 px-5 py-4 backdrop-blur-sm">
                <div className="flex min-w-0 items-start gap-2.5">
                  <TriangleAlert
                    className="mt-0.5 h-4 w-4 shrink-0 text-amber-400"
                    aria-hidden
                  />
                  <div className="min-w-0">
                    <p className="text-[14px] font-medium text-white">
                      {failure === "no_live_site"
                        ? "No live site to preview"
                        : "The live site isn't responding"}
                    </p>
                    <p className="mt-0.5 text-[13px] leading-relaxed text-zinc-300">
                      Showing the latest screenshot instead. Cloning still works —
                      you get the full template.
                    </p>
                  </div>
                </div>
                {cloneButton}
              </div>
            </div>
          )}

          {showIframe && frameUrl && (
            <>
              <iframe
                src={frameUrl}
                title={title}
                onLoad={handleIframeLoad}
                // Same preview, same external links: an opened tab inherits
                // these flags unless it is allowed to escape, and a half-
                // sandboxed third-party destination fails a step in.
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
                // Same reason as the workspace preview iframe: `clipboard-write`
                // defaults to `self`, so a copy button inside this cross-origin
                // embed is blocked by permissions policy unless we delegate it.
                // Write only — never `clipboard-read`.
                allow="clipboard-write"
                referrerPolicy="no-referrer"
                className="h-full w-full border-0"
              />
              {/* Cover while the frame loads AND while its health is unverified:
                  the project's screenshot (the API filter requires screenshot_url
                  alongside vercel_url, so it's effectively always present) so the
                  modal isn't a blank pulsing box — and, crucially, so a dead
                  deployment is never uncovered in the first place. Once the frame
                  has loaded and the probe says it's serving, this unmounts and
                  the live site shows through. Fall back to the prior pulsing
                  skeleton only on the (unlikely) path where screenshot is
                  missing. */}
              {!revealIframe && screenshotSrc && (
                <div className="pointer-events-none absolute inset-0 overflow-hidden">
                  <Image
                    src={screenshotSrc}
                    alt=""
                    fill
                    className="object-cover object-top"
                    unoptimized
                    sizes="70vw"
                  />
                </div>
              )}
              {!revealIframe && !screenshotSrc && (
                <div className="pointer-events-none absolute inset-0 animate-pulse bg-zinc-100 dark:bg-white/[0.04]" />
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
